import moment from 'moment';
import { IKpiSnapshot } from '@/types/response';

export type Granularity = 'day' | 'week' | 'month';

/**
 * 週次・月次へのまとめ方。
 * - sum: その日に起きた件数（新規・投稿など）。期間の合計
 * - avg: その日の UU（歩数アクティブなど）。日をまたいで足すと同じ人を重複して数えるので日平均
 * - last: 時点の値（総数・MAU・割合など）。期間の最終日の値（月次の mauMtd＝その月の MAU）
 * - delta: 時点の値の増減。期間の最終日の値 − 前の期間の最終日の値（日次なら前日比）。
 *   前の期間に値が無いとき（表示期間の先頭・集計の欠け）は出さない
 */
export type Aggregation = 'sum' | 'avg' | 'last' | 'delta';

export interface IMetric {
  id: string;
  label: string;
  get: (row: IKpiSnapshot) => number | null | undefined;
  agg: Aggregation;
}

export interface IBucket {
  /** 軸ラベル（日次 MM/DD、週次 週初め MM/DD〜、月次 YYYY/MM） */
  label: string;
  /** 期間の初日（YYYY-MM-DD）。年をまたいでも一意なので、期間の特定にはこちらを使う */
  start: string;
  from: string;
  to: string;
  /** 期間内で行があった日数と、期間の本来の日数（途中の週・月や欠損日の判定用） */
  days: number;
  expectedDays: number;
  values: Record<string, number | null>;
}

const bucketStart = (date: string, g: Granularity): moment.Moment => {
  const m = moment(date, 'YYYY-MM-DD');
  if (g === 'week') return m.startOf('isoWeek');
  if (g === 'month') return m.startOf('month');

  return m;
};

const bucketLabel = (start: moment.Moment, g: Granularity): string => {
  if (g === 'week') return `${start.format('MM/DD')}〜`;
  if (g === 'month') return start.format('YYYY/MM');

  return start.format('MM/DD');
};

/** その日が入る期間の初日（toBuckets の start と同じ） */
export const bucketStartOf = (date: string, g: Granularity): string =>
  bucketStart(date, g).format('YYYY-MM-DD');

const expectedDaysOf = (start: moment.Moment, g: Granularity): number => {
  if (g === 'week') return 7;
  if (g === 'month') return start.daysInMonth();

  return 1;
};

const aggregate = (values: number[], agg: 'sum' | 'avg'): number | null => {
  if (!values.length) return null;

  const sum = values.reduce((a, b) => a + b, 0);
  if (agg === 'sum') return sum;

  return Math.round((sum / values.length) * 10) / 10;
};

const isNumber = (v: unknown): v is number => typeof v === 'number' && !Number.isNaN(v);

/** rows は targetDate の古い順（API の並び）を前提にする */
export const toBuckets = (
  rows: IKpiSnapshot[],
  metrics: IMetric[],
  g: Granularity,
): IBucket[] => {
  const groups: { start: moment.Moment; rows: IKpiSnapshot[] }[] = [];

  rows.forEach((row) => {
    const start = bucketStart(row.targetDate, g);
    const current = groups[groups.length - 1];
    if (current && current.start.isSame(start, 'day')) {
      current.rows.push(row);
    } else {
      groups.push({ start, rows: [row] });
    }
  });

  // 集計の無い日・週・月も空の点として入れる。入れないと前後が詰まって線がつながり、
  // 欠けた期間が見えなくなる
  const unit = g === 'week' ? 'isoWeek' : g;
  const filled: { start: moment.Moment; rows: IKpiSnapshot[] }[] = [];
  groups.forEach((group) => {
    const prev = filled[filled.length - 1];
    if (prev) {
      for (
        const d = prev.start.clone().add(1, g === 'week' ? 'week' : g);
        d.isBefore(group.start, 'day');
        d.add(1, g === 'week' ? 'week' : g)
      ) {
        filled.push({ start: d.clone().startOf(unit), rows: [] });
      }
    }
    filled.push(group);
  });

  const buckets = filled.map(({ start, rows: bucketRows }) => {
    const values: Record<string, number | null> = {};
    if (!bucketRows.length) {
      metrics.forEach((metric) => {
        values[metric.id] = null;
      });

      return {
        label: bucketLabel(start, g),
        start: start.format('YYYY-MM-DD'),
        from: start.format('YYYY-MM-DD'),
        to: start.format('YYYY-MM-DD'),
        days: 0,
        expectedDays: expectedDaysOf(start, g),
        values,
      };
    }

    metrics.forEach((metric) => {
      const raw = bucketRows.map(metric.get);
      if (metric.agg === 'last' || metric.agg === 'delta') {
        // キーが無い行（取り込み行）は飛ばすが、null（分母0など）は最終日の値として残す。
        // 飛ばすと前の日の値を「期間の最終日の値」として出してしまう
        const present = raw.filter((v) => v !== undefined);
        const last = present[present.length - 1];
        values[metric.id] = isNumber(last) ? last : null;
      } else {
        values[metric.id] = aggregate(raw.filter(isNumber), metric.agg);
      }
    });

    return {
      label: bucketLabel(start, g),
      start: start.format('YYYY-MM-DD'),
      from: bucketRows[0].targetDate,
      to: bucketRows[bucketRows.length - 1].targetDate,
      days: bucketRows.length,
      expectedDays: expectedDaysOf(start, g),
      values,
    };
  });

  // delta は上で各期間の最終日の値を入れてあるので、前の期間との差に置き換える
  metrics
    .filter((metric) => metric.agg === 'delta')
    .forEach((metric) => {
      let prev: number | null = null;
      buckets.forEach((bucket) => {
        const current = bucket.values[metric.id];
        bucket.values[metric.id] =
          isNumber(current) && isNumber(prev) ? Math.round((current - prev) * 10) / 10 : null;
        prev = current;
      });
    });

  return buckets;
};

/** 最初の行から最後の行までのうち、行が無い日（バッチ失敗・取りこぼし） */
export const findMissingDates = (rows: IKpiSnapshot[]): string[] => {
  if (rows.length < 2) return [];

  const have = new Set(rows.map((r) => r.targetDate));
  const missing: string[] = [];
  const end = moment(rows[rows.length - 1].targetDate, 'YYYY-MM-DD');
  for (
    const d = moment(rows[0].targetDate, 'YYYY-MM-DD');
    d.isBefore(end, 'day');
    d.add(1, 'day')
  ) {
    const date = d.format('YYYY-MM-DD');
    if (!have.has(date)) missing.push(date);
  }

  return missing;
};
